import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Input, Button, message } from 'antd';
import { LockOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import Auth from '../services/AuthService';
import flagImg from './assets/footer/flag.png';

export default function ResetPassword() {
    const location = useLocation();
    const history  = useHistory();
    const token    = new URLSearchParams(location.search).get('token') || '';

    const [step,       setStep]       = useState('validating'); // validating | form | success | invalid
    const [password,   setPassword]   = useState('');
    const [confirm,    setConfirm]    = useState('');
    const [loading,    setLoading]    = useState(false);
    const [userEmail,  setUserEmail]  = useState('');
    const [showPw,     setShowPw]     = useState(false);
    const [showCnf,    setShowCnf]    = useState(false);

    useEffect(() => {
        if (!token) { setStep('invalid'); return; }
        Auth.validateResetToken(token)
            .then(res => {
                if (res.data?.code === 1 || res.data?.code === 200) {
                    setUserEmail(res.data.email || '');
                    setStep('form');
                } else {
                    setStep('invalid');
                }
            })
            .catch(() => setStep('invalid'));
    }, [token]);

    const handleReset = async () => {
        if (!password) { message.error('Please enter a new password'); return; }
        if (password.length < 6) { message.error('Password must be at least 6 characters'); return; }
        if (password !== confirm) { message.error('Passwords do not match'); return; }
        setLoading(true);
        try {
            const res = await Auth.resetPassword({ token, password, confirm });
            if (res.data?.code === 1 || res.data?.code === 200) {
                setStep('success');
            } else {
                message.error(res.data?.message || 'Reset failed');
            }
        } catch(e) {
            message.error(e.message || 'Reset failed');
        } finally {
            setLoading(false);
        }
    };

    const strength = () => {
        if (!password) return null;
        if (password.length < 6) return { label:'Too short', color:'#ef4444', pct:20 };
        if (password.length < 8) return { label:'Weak', color:'#f97316', pct:40 };
        const has = { upper:/[A-Z]/.test(password), lower:/[a-z]/.test(password), num:/[0-9]/.test(password), sym:/[^A-Za-z0-9]/.test(password) };
        const score = Object.values(has).filter(Boolean).length;
        if (score === 4) return { label:'Strong', color:'#22c55e', pct:100 };
        if (score === 3) return { label:'Good', color:'#84cc16', pct:75 };
        return { label:'Fair', color:'#eab308', pct:55 };
    };
    const pw = strength();

    return (
        <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#0d2137 0%,#1e3a5f 50%,#0d2137 100%)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
            <div style={{ width:'100%', maxWidth:440 }}>
                {/* Header */}
                <div style={{ textAlign:'center', marginBottom:28 }}>
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:12, marginBottom:12 }}>
                        <img src={flagImg} alt="Flag" style={{ height:36, borderRadius:4, boxShadow:'0 2px 8px rgba(0,0,0,0.3)' }}/>
                        <div>
                            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:10, letterSpacing:2, textTransform:'uppercase' }}>Kingdom of Eswatini</div>
                            <div style={{ color:'#fff', fontSize:18, fontWeight:800 }}>Ministry of Health</div>
                            <div style={{ color:'rgba(255,255,255,0.6)', fontSize:11 }}>National Quality Management Programme</div>
                        </div>
                    </div>
                </div>

                <div style={{ background:'#fff', borderRadius:16, padding:'36px 32px', boxShadow:'0 20px 60px rgba(0,0,0,0.3)' }}>

                    {/* Validating */}
                    {step === 'validating' && (
                        <div style={{ textAlign:'center', padding:'20px 0' }}>
                            <div style={{ fontSize:32, marginBottom:12 }}>🔐</div>
                            <div style={{ fontWeight:700, fontSize:16, color:'#1e293b' }}>Validating reset link...</div>
                            <div style={{ color:'#94a3b8', fontSize:13, marginTop:6 }}>Please wait</div>
                        </div>
                    )}

                    {/* Invalid token */}
                    {step === 'invalid' && (
                        <div style={{ textAlign:'center', padding:'20px 0' }}>
                            <CloseCircleOutlined style={{ fontSize:48, color:'#ef4444', display:'block', marginBottom:16 }}/>
                            <div style={{ fontWeight:700, fontSize:18, color:'#1e293b', marginBottom:8 }}>Link Invalid or Expired</div>
                            <div style={{ color:'#64748b', fontSize:14, lineHeight:1.6, marginBottom:24 }}>
                                This password reset link is invalid or has expired. Reset links are valid for 1 hour only.
                            </div>
                            <Button type="primary" size="large" style={{ width:'100%' }} onClick={() => history.push('/')}>
                                Back to Login
                            </Button>
                        </div>
                    )}

                    {/* Reset form */}
                    {step === 'form' && (
                        <>
                            <div style={{ marginBottom:24 }}>
                                <LockOutlined style={{ fontSize:28, color:'#1890ff', display:'block', marginBottom:8 }}/>
                                <div style={{ fontWeight:800, fontSize:20, color:'#1e293b' }}>Set New Password</div>
                                {userEmail && <div style={{ color:'#64748b', fontSize:13, marginTop:4 }}>for <strong>{userEmail}</strong></div>}
                            </div>

                            <div style={{ marginBottom:16 }}>
                                <label style={{ display:'block', fontWeight:600, fontSize:12, color:'#475569', marginBottom:6 }}>New Password *</label>
                                <Input.Password
                                    size="large" value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Minimum 6 characters"
                                    visibilityToggle={{ visible: showPw, onVisibleChange: setShowPw }}
                                    onPressEnter={handleReset}
                                />
                                {pw && (
                                    <div style={{ marginTop:6 }}>
                                        <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                                            <span style={{ color:'#94a3b8' }}>Password strength</span>
                                            <span style={{ color: pw.color, fontWeight:700 }}>{pw.label}</span>
                                        </div>
                                        <div style={{ height:4, background:'#f1f5f9', borderRadius:2 }}>
                                            <div style={{ height:4, width: pw.pct+'%', background: pw.color, borderRadius:2, transition:'all 0.3s' }}/>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={{ marginBottom:24 }}>
                                <label style={{ display:'block', fontWeight:600, fontSize:12, color:'#475569', marginBottom:6 }}>Confirm Password *</label>
                                <Input.Password
                                    size="large" value={confirm}
                                    onChange={e => setConfirm(e.target.value)}
                                    placeholder="Re-enter your password"
                                    visibilityToggle={{ visible: showCnf, onVisibleChange: setShowCnf }}
                                    onPressEnter={handleReset}
                                    status={confirm && confirm !== password ? 'error' : ''}
                                />
                                {confirm && confirm !== password && (
                                    <div style={{ fontSize:11, color:'#ef4444', marginTop:4 }}>Passwords do not match</div>
                                )}
                            </div>

                            <Button type="primary" size="large" loading={loading}
                                style={{ width:'100%', height:48, fontWeight:700, fontSize:15 }}
                                onClick={handleReset}>
                                Reset Password
                            </Button>

                            <div style={{ textAlign:'center', marginTop:16 }}>
                                <a onClick={() => history.push('/')} style={{ color:'#94a3b8', fontSize:12, cursor:'pointer' }}>
                                    Back to Login
                                </a>
                            </div>
                        </>
                    )}

                    {/* Success */}
                    {step === 'success' && (
                        <div style={{ textAlign:'center', padding:'20px 0' }}>
                            <CheckCircleOutlined style={{ fontSize:48, color:'#22c55e', display:'block', marginBottom:16 }}/>
                            <div style={{ fontWeight:700, fontSize:20, color:'#1e293b', marginBottom:8 }}>Password Reset!</div>
                            <div style={{ color:'#64748b', fontSize:14, lineHeight:1.6, marginBottom:24 }}>
                                Your password has been changed successfully. You can now log in with your new password.
                            </div>
                            <Button type="primary" size="large" style={{ width:'100%', height:48, fontWeight:700 }}
                                onClick={() => history.push('/')}>
                                Go to Login
                            </Button>
                        </div>
                    )}
                </div>

                <div style={{ textAlign:'center', marginTop:16, color:'rgba(255,255,255,0.4)', fontSize:11 }}>
                    QoC Platform · Ministry of Health · Eswatini
                </div>
            </div>
        </div>
    );
}

